/**
 * Sets up a state object that can be subscribed to
 *
 * @param {Object} initialState - The initial keys we are going to be tracking
 */
export function ReactiveState(initialState) {
  this.internalState = {};
  Object.entries(initialState).forEach(([key, initialValue]) => this.makeKeyReactive(key, initialValue));
}
/**
 * Internally we use a different key to the public key, this is just a helper to get that
 *
 * @param {String} key - The key from the state tree we want
 *
 * @returns {String} The key prefixed with an `_`
 */

ReactiveState.prototype.getPrivateKey = function getPrivateKey(key) {
  return "_".concat(key);
};
/**
 * This allows you to add keys to be tracked after initialisation
 *
 * @param {String} key - The key from the state tree we want
 * @param {*} initialValue - The initial value for this key
 */


ReactiveState.prototype.addKey = function addKey(key, initialValue) {
  this.makeKeyReactive(key, initialValue);
};
/**
 * This will return whether a key has been set in the state
 *
 * @param {String} key - The key from the state tree we want
 *
 * @returns {Boolean}
 */


ReactiveState.prototype.hasKey = function hasKey(key) {
  return this.checkIfKeyInState(key, false);
};
/**
 * This is the magic that allows us to subscribe to state changes.
 *
 * This will take the key and add a getter and setter to it so we can notify all subscriptions
 * when the value gets set
 *
 * @param {String} key - The key from the state tree we want
 * @param {*} initialValue - The initial value for this key
 *
 * @returns {Object} The internal state
 */


ReactiveState.prototype.makeKeyReactive = function makeKeyReactive(key, initialValue) {
  const _this = this;

  const privateKey = this.getPrivateKey(key);
  this.internalState[privateKey] = {
    value: initialValue,
    listeners: []
  };
  Object.defineProperty(this, key, {
    get() {
      return _this.internalState[privateKey].value;
    },

    set(val) {
      _this.notify(key, val, {
        prevVal: _this.internalState[privateKey].value
      });

      _this.internalState[privateKey].value = val;
    }

  });
  return this.internalState;
};
/**
 * Subscribe to a key to get notified when it's value changes
 *
 * @param {String} key - The key from the state tree we want
 * @param {Function} fnc - The function that will get run when the key's value changes
 */


ReactiveState.prototype.subscribe = function subscribe(key, fnc) {
  if (this.checkIfKeyInState(key)) {
    const privateKey = this.getPrivateKey(key);
    this.internalState[privateKey].listeners.push(fnc);
  }
};
/**
 * Stop getting updates when this key's value changes
 *
 * @param {String} key - The key from the state tree we want
 * @param {Function} fnc - The function that will get run when the key's value changes
 */


ReactiveState.prototype.unsubscribe = function unsubscribe(key, fnc) {
  if (this.checkIfKeyInState(key)) {
    const privateKey = this.getPrivateKey(key);
    /* prettier-ignore */

    this.internalState[privateKey].listeners = this.internalState[privateKey].listeners.filter(fn => fn !== fnc);
  }
};
/**
 * This will run all of the subscribed functions to a key
 *
 * @param {String} key - The key from the state tree we want
 * @param {*} val - The new value
 * @param {*} [prevVal] - The previous value
 */


ReactiveState.prototype.notify = function notify(key, val, {
  prevVal
}) {
  if (this.checkIfKeyInState(key)) {
    const privateKey = this.getPrivateKey(key);
    this.internalState[privateKey].listeners.forEach(fn => fn(val, {
      prevVal
    }));
  }
};
/**
 * Before we doing anything regarding keys internally we want to see if it exists
 *
 * @param {String} key - The key from the state tree we want
 * @param {Boolean} [throwError=true] - Whether to throw an error if the key doesn't exist
 */


ReactiveState.prototype.checkIfKeyInState = function checkIfKeyInState(key, throwError = true) {
  const privateKey = this.getPrivateKey(key);
  const keyInState = (privateKey in this.internalState);

  if (keyInState === false && throwError) {
    throw new Error("".concat(key, " doesn't exist in ObservableState"));
  }

  return keyInState;
};